<template>
  <v-container fluid class="pl-4 pr-4 pb-4 pt-1">
    <!-- Compact header with bulk actions -->
    <v-row align="center" class="mb-0 px-0" style="min-height: 48px;">
      <v-col cols="auto">
        <v-select
          v-model="selectedBulkAction"
          :items="bulkActions"
          item-title="text"
          item-value="value"
          placeholder="Выберите действие"
          variant="outlined"
          density="compact"
          hide-details
          clearable
          :disabled="selectedItems.length === 0"
          @update:model-value="executeBulkAction"
          style="min-width: 200px"
        ></v-select>
      </v-col>
      <v-col class="text-center">
        <h4 class="mb-0">Управление пользователями</h4>
      </v-col>
      <v-col cols="auto">
        <div class="d-flex align-center ga-2">
          <span class="text-caption">На странице:</span>
          <v-select
            v-model="limit"
            :items="[5, 10, 20, 50, 100]"
            variant="outlined"
            density="compact"
            hide-details
            style="max-width: 160px"
            @update:model-value="onPageChange(1)"
          ></v-select>
          <v-pagination
            v-model="currentPage"
            :length="totalPages"
            :total-visible="5"
            size="small"
            @update:model-value="onPageChange"
          ></v-pagination>
          <span class="text-caption font-weight-bold">Найдено: {{ totalItems }}</span>
        </div>
      </v-col>
    </v-row>

    <!-- Себестоимость: общая сводка -->
    <div class="d-flex align-center flex-wrap ga-4 px-1 pb-1 text-caption">
      <span>Себестоимость всего: <b>{{ formatMoney(usageOverview.total_cost_rub) }} ₽</b> ({{ formatMoney(usageOverview.total_cost_usd) }} $)</span>
      <span>из них общие расходы: <b>{{ formatMoney(usageOverview.shared_cost_rub) }} ₽</b></span>
      <span v-if="usageOverview.usd_rate">курс доллара: {{ formatMoney(usageOverview.usd_rate) }} ₽ на {{ formatDay(usageOverview.usd_rate_date) }}</span>
      <span v-if="usageOverview.usage_since">учёт с {{ formatDay(usageOverview.usage_since) }}</span>
      <span v-if="usageOverview.stats_calculated_at">срез: {{ formatDate(usageOverview.stats_calculated_at) }}</span>
      <span v-if="usageOverview.unpriced_requests" class="text-warning">без цены: {{ usageOverview.unpriced_requests }} запросов</span>
      <v-btn size="x-small" variant="outlined" to="/admin/llm-costs">Расходы LLM и цены</v-btn>
    </div>

    <!-- Table section -->
    <v-card>
      <v-card-text class="pa-0">
        <v-data-table
          :headers="headers"
          :items="users"
          :items-per-page="-1"
          :loading="isLoading"
          :sort-by="sortBy"
          @update:sort-by="updateSort"
          @click:row="openUsageDialog"
          class="elevation-1 users-table"
          hover
          fixed-header
          height="calc(100vh - 266px)"
          hide-default-footer
        >
          <!-- Master checkbox header -->
          <template v-slot:[`header.checkbox`]>
            <v-checkbox
              :model-value="isAllSelected"
              :indeterminate="isIndeterminate"
              hide-details
              density="compact"
              @update:model-value="toggleSelectAll"
            ></v-checkbox>
          </template>

          <!-- Checkbox column -->
          <template v-slot:[`item.checkbox`]="{ item }">
            <v-checkbox
              v-model="selectedItems"
              :value="item.id"
              hide-details
              density="compact"
            ></v-checkbox>
          </template>

          <!-- Username column -->
          <template v-slot:[`item.username`]="{ item }">
            <div class="d-flex align-center">
              <v-avatar size="32" class="mr-2">
                <v-icon>mdi-account-circle</v-icon>
              </v-avatar>
              <div>
                <div class="font-weight-medium">{{ item.username }}</div>
                <div class="text-caption text-disabled">ID: {{ item.id }}</div>
              </div>
            </div>
          </template>

          <!-- Status column -->
          <template v-slot:[`item.status`]="{ item }">
            <v-chip
              :color="getStatusChipColor(item)"
              variant="tonal"
              size="small"
            >
              {{ getStatusText(item) }}
            </v-chip>
          </template>

          <!-- Email verification column -->
          <template v-slot:[`item.email_verified`]="{ item }">
            <v-icon
              :color="item.is_email_confirmed ? 'success' : 'warning'"
              size="small"
              :title="item.is_email_confirmed ? 'Почта подтверждена' : 'Почта не подтверждена'"
            >{{ item.is_email_confirmed ? 'mdi-email-check' : 'mdi-email-alert' }}</v-icon>
          </template>

          <!-- Roles column -->
          <!-- tariff plan: empty - the default one; admins have no limits -->
          <template v-slot:[`item.plan`]="{ item }">
            <span v-if="(item.roles || []).includes('admin')" class="text-caption text-disabled">без ограничений</span>
            <v-select v-else :model-value="item.plan_id" :items="planItems" item-title="title" item-value="id"
                      density="compact" variant="plain" hide-details @update:model-value="setPlan(item, $event)"></v-select>
          </template>

          <template v-slot:[`item.roles`]="{ item }">
            <div v-if="item.roles && item.roles.length > 0">
              <v-chip
                v-for="role in item.roles.slice(0, 2)"
                :key="role"
                size="x-small"
                class="mr-1"
                color="primary"
                variant="outlined"
              >
                {{ role }}
              </v-chip>
              <v-chip
                v-if="item.roles.length > 2"
                size="x-small"
                color="grey"
                variant="outlined"
              >
                +{{ item.roles.length - 2 }}
              </v-chip>
            </div>
            <span v-else class="text-caption text-disabled">Нет ролей</span>
          </template>

          <!-- Usage columns -->
          <template v-slot:[`item.messages_total`]="{ item }">
            <span class="text-caption">{{ formatNumber(item.messages_total) }}</span>
          </template>
          <template v-slot:[`item.cost_rub_month`]="{ item }">
            <span class="text-caption">{{ formatMoney(item.cost_rub_month) }}</span>
          </template>
          <template v-slot:[`item.cost_rub_total`]="{ item }">
            <span class="text-caption">{{ formatMoney(item.cost_rub_total) }}</span>
          </template>
          <template v-slot:[`item.storage_bytes_total`]="{ item }">
            <span class="text-caption text-no-wrap">{{ formatBytes(item.storage_bytes_total) }}</span>
          </template>

          <!-- Created at column -->
          <template v-slot:[`item.created_at`]="{ item }">
            <span class="text-caption">{{ formatDate(item.created_at) }}</span>
          </template>

          <!-- Last login column -->
          <template v-slot:[`item.last_login`]="{ item }">
            <span v-if="item.last_login" class="text-caption">{{ formatDate(item.last_login) }}</span>
            <span v-else class="text-caption text-disabled">Никогда</span>
          </template>

          <!-- Notes column -->
          <template v-slot:[`item.notes`]="{ item }">
            <v-btn
              icon="mdi-note-text"
              variant="text"
              size="small"
              @click="openNotesDialog(item)"
              :title="item.admin_notes ? 'Заметки' : 'Добавить заметку'"
              :color="item.admin_notes ? 'info' : 'grey'"
            ></v-btn>
          </template>

          <!-- Actions column -->
          <template v-slot:[`item.actions`]="{ item }">
            <div class="d-flex">
              <v-btn
                :icon="item.is_active ? 'mdi-account-check' : 'mdi-account-off'"
                variant="text"
                size="small"
                @click="toggleUserStatus(item)"
                :color="item.is_active ? 'success' : 'error'"
              ></v-btn>
              <v-btn
                icon="mdi-shield-account"
                variant="text"
                size="small"
                color="primary"
                @click="manageRoles(item)"
              ></v-btn>
              <v-btn
                icon="mdi-devices"
                variant="text"
                size="small"
                color="primary"
                title="Сессии и журнал входов"
                @click="openSessions(item)"
              ></v-btn>
              <!-- «Просмотр как пользователь» (task 60): read only; never an admin (the server checks it too) -->
              <v-btn
                v-if="!(item.roles || []).includes('admin')"
                icon="mdi-eye"
                variant="text"
                size="small"
                color="primary"
                title="Просмотреть как пользователь (только чтение, 30 минут)"
                :loading="viewAsBusy === item.id"
                @click="viewAs(item)"
              ></v-btn>
              <!-- only a user without projects; never an admin (the server checks it too) -->
              <v-btn
                icon="mdi-delete"
                variant="text"
                size="small"
                color="error"
                title="Удалить пользователя (только без проектов)"
                :disabled="(item.roles || []).includes('admin')"
                @click="deleteUser(item)"
              ></v-btn>
            </div>
          </template>

        </v-data-table>
      </v-card-text>
    </v-card>

    <!-- Filter Panel -->
    <v-navigation-drawer
      v-model="showFilterPanel"
      :rail="!panelVisible"
      location="right"
      permanent
      @click="expandPanel"
    >
      <!-- Expanded state -->
      <template v-if="panelVisible">
        <v-list-item class="px-2" density="compact">
          <v-list-item-title class="text-h6">Фильтры</v-list-item-title>
          <template v-slot:append>
            <v-btn
              variant="text"
              icon
              @click.stop="togglePanel"
            >
              <v-icon>mdi-menu-open</v-icon>
            </v-btn>
          </template>
        </v-list-item>
        
        <v-divider></v-divider>
        
        <div class="pa-3">
          <!-- Search -->
          <v-text-field
            v-model="userFilters.searchQuery"
            label="Поиск по имени или email"
            variant="outlined"
            density="compact"
            hide-details
            class="mb-4"
            prepend-inner-icon="mdi-magnify"
            clearable
            @input="onFilterChange"
          ></v-text-field>

          <!-- Status filters -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">Статус пользователя</div>
            <v-select
              v-model="userFilters.isActive"
              :items="[
                { text: 'Все', value: null },
                { text: 'Активные', value: true },
                { text: 'Неактивные', value: false }
              ]"
              item-title="text"
              item-value="value"
              label="Активность"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>
            
            <v-select
              v-model="userFilters.isEmailConfirmed"
              :items="[
                { text: 'Все', value: null },
                { text: 'Email подтвержден', value: true },
                { text: 'Email не подтвержден', value: false }
              ]"
              item-title="text"
              item-value="value"
              label="Подтверждение email"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>
            
            <v-select
              v-model="userFilters.isSuperuser"
              :items="[
                { text: 'Все', value: null },
                { text: 'Суперпользователи', value: true },
                { text: 'Обычные пользователи', value: false }
              ]"
              item-title="text"
              item-value="value"
              label="Права администратора"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>
          </div>

          <!-- Additional filters -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">Дополнительные фильтры</div>
            <v-select
              v-model="userFilters.hasRoles"
              :items="[
                { text: 'Все', value: null },
                { text: 'С ролями', value: true },
                { text: 'Без ролей', value: false }
              ]"
              item-title="text"
              item-value="value"
              label="Наличие ролей"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>
            
            <v-select
              v-model="userFilters.hasNotes"
              :items="[
                { text: 'Все', value: null },
                { text: 'С примечаниями', value: true },
                { text: 'Без примечаний', value: false }
              ]"
              item-title="text"
              item-value="value"
              label="Наличие примечаний"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>
            
            <v-select
              v-model="userFilters.inviteCodeUsed"
              :items="[
                { text: 'Все', value: null },
                { text: 'Зарегистрированы по приглашению', value: true },
                { text: 'Без приглашения', value: false }
              ]"
              item-title="text"
              item-value="value"
              label="Способ регистрации"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              clearable
              @update:model-value="onFilterChange"
            ></v-select>
          </div>

          <!-- Date ranges -->
          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">Дата регистрации</div>
            <v-text-field
              v-model="userFilters.createdDateFrom"
              type="date"
              label="С"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              @change="onFilterChange"
            ></v-text-field>
            <v-text-field
              v-model="userFilters.createdDateTo"
              type="date"
              label="По"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              @change="onFilterChange"
            ></v-text-field>
          </div>

          <div class="mb-4">
            <div class="text-subtitle-2 mb-2">Последний вход</div>
            <v-text-field
              v-model="userFilters.lastLoginFrom"
              type="date"
              label="С"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              @change="onFilterChange"
            ></v-text-field>
            <v-text-field
              v-model="userFilters.lastLoginTo"
              type="date"
              label="По"
              variant="outlined"
              density="compact"
              hide-details
              class="mb-4"
              @change="onFilterChange"
            ></v-text-field>
          </div>

          <!-- Clear filters button -->
          <v-btn
            color="error"
            variant="outlined"
            block
            @click="clearFilters"
          >
            <v-icon start>mdi-filter-off</v-icon>
            Очистить
          </v-btn>
        </div>
      </template>

      <!-- Collapsed state -->
      <template v-else>
        <div class="d-flex justify-center">
          <v-btn
            variant="text"
            icon
            @click.stop="togglePanel"
            class="ma-2"
          >
            <v-icon>mdi-menu</v-icon>
          </v-btn>
        </div>
        <v-divider></v-divider>
      </template>
    </v-navigation-drawer>

    <!-- Usage dialogs -->
    <UserUsageDialog v-model="showUsageDialog" :user="usageDialogUser" />

    <!-- Notes Dialog -->
    <v-dialog v-model="showNotesDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Примечания пользователя {{ notesDialogUser?.username }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeNotesDialog"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <v-textarea
            v-model="notesText"
            label="Административные примечания"
            variant="outlined"
            rows="5"
            placeholder="Добавьте примечания об этом пользователе..."
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeNotesDialog"
            :disabled="isSubmitting"
          >
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            @click="saveNotes"
            :loading="isSubmitting"
          >
            Сохранить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Roles Management Dialog -->
    <v-dialog v-model="showRolesDialog" max-width="700px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">Управление ролями пользователя {{ rolesDialogUser?.username }}</span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeRolesDialog"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <v-row>
            <!-- Available Roles Column -->
            <v-col cols="6">
              <div class="text-subtitle-1 mb-2">Доступные роли</div>
              <v-card variant="outlined" style="min-height: 300px;">
                <v-card-text class="pa-2">
                  <v-list density="compact" v-if="!isLoadingRoles">
                    <v-list-item
                      v-for="role in availableRolesFiltered"
                      :key="role.id"
                      @click="assignRole(role)"
                      class="cursor-pointer"
                    >
                      <template v-slot:prepend>
                        <v-icon color="primary">mdi-plus-circle-outline</v-icon>
                      </template>
                      <v-list-item-title>{{ role.name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="role.description">{{ role.description }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="availableRolesFiltered.length === 0">
                      <v-list-item-title class="text-disabled">Все роли назначены</v-list-item-title>
                    </v-list-item>
                  </v-list>
                  <div v-else class="text-center pa-4">
                    <v-progress-circular indeterminate></v-progress-circular>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- User Roles Column -->
            <v-col cols="6">
              <div class="text-subtitle-1 mb-2">Роли пользователя</div>
              <v-card variant="outlined" style="min-height: 300px;">
                <v-card-text class="pa-2">
                  <v-list density="compact" v-if="!isLoadingRoles">
                    <v-list-item
                      v-for="role in userRoles"
                      :key="role.id"
                      @click="removeRole(role)"
                      class="cursor-pointer"
                    >
                      <template v-slot:prepend>
                        <v-icon color="error">mdi-minus-circle-outline</v-icon>
                      </template>
                      <v-list-item-title>{{ role.name }}</v-list-item-title>
                      <v-list-item-subtitle v-if="role.description">{{ role.description }}</v-list-item-subtitle>
                    </v-list-item>
                    <v-list-item v-if="userRoles.length === 0">
                      <v-list-item-title class="text-disabled">Роли не назначены</v-list-item-title>
                    </v-list-item>
                  </v-list>
                  <div v-else class="text-center pa-4">
                    <v-progress-circular indeterminate></v-progress-circular>
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeRolesDialog"
            :disabled="isSubmitting"
          >
            Закрыть
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Bulk Role Operations Dialog -->
    <v-dialog v-model="showBulkRoleDialog" max-width="500px" persistent>
      <v-card>
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-h5">
            {{ bulkRoleAction === 'assign_role' ? 'Назначить роль' : 'Удалить роль' }}
          </span>
          <v-btn
            icon="mdi-close"
            variant="text"
            size="small"
            @click="closeBulkRoleDialog"
          ></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="mb-4">
            {{ bulkRoleAction === 'assign_role' ? 'Назначить роль' : 'Удалить роль' }} 
            для {{ selectedItems.length }} выбранных пользователей
          </div>
          
          <v-select
            v-model="selectedRole"
            :items="bulkAvailableRoles"
            item-title="name"
            item-value="id"
            label="Выберите роль"
            variant="outlined"
            :loading="isLoadingRoles"
            return-object
          ></v-select>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            text
            @click="closeBulkRoleDialog"
            :disabled="isSubmitting"
          >
            Отмена
          </v-btn>
          <v-btn
            color="primary"
            @click="executeBulkRoleAction"
            :loading="isSubmitting"
            :disabled="!selectedRole"
          >
            {{ bulkRoleAction === 'assign_role' ? 'Назначить' : 'Удалить' }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Success Snackbar -->
    <v-snackbar
      v-model="showSuccessSnackbar"
      color="success"
      timeout="3000"
      location="top"
    >
      {{ successMessage }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showSuccessSnackbar = false"
        >
          X
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Error Snackbar -->
    <v-snackbar
      v-model="showErrorSnackbar"
      color="error"
      timeout="5000"
      location="top"
    >
      {{ errorMessage }}
      <template v-slot:actions>
        <v-btn
          variant="text"
          @click="showErrorSnackbar = false"
        >
          X
        </v-btn>
      </template>
    </v-snackbar>
    <!-- Sessions of a user: where he is signed in; end one or all -->
    <v-dialog v-model="sessions.visible" max-width="760px">
      <v-card>
        <v-card-title class="d-flex align-center">
          <span class="text-h5">Сессии: {{ sessions.user?.email }}</span>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" @click="sessions.visible = false"></v-btn>
        </v-card-title>
        <v-card-text>
          <v-table density="compact">
            <thead>
              <tr>
                <th>Начало</th>
                <th>Последняя активность</th>
                <th>IP-адрес</th>
                <th>Устройство</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="s in sessions.items" :key="s.session_id">
                <td>{{ formatSessionTime(s.started) }}</td>
                <td>{{ formatSessionTime(s.last_seen) }}</td>
                <td>{{ s.ip || '—' }}</td>
                <td>{{ s.device || '—' }}</td>
                <td class="text-right">
                  <v-btn size="small" variant="text" color="error" :loading="sessions.busy === s.session_id"
                         @click="endSession(s)">Завершить</v-btn>
                </td>
              </tr>
              <tr v-if="!sessions.items.length && !sessions.loading">
                <td colspan="5" class="text-center text-disabled">Активных сессий нет</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>
        <v-card-actions>
          <v-btn variant="text" :to="{ path: '/admin/auth-events', query: { user_id: sessions.user?.id, user_email: sessions.user?.email } }">Журнал входов пользователя</v-btn>
          <v-spacer></v-spacer>
          <v-btn color="error" variant="elevated" :disabled="!sessions.items.length" :loading="sessions.busy === 'all'"
                 @click="endAllSessions">Завершить все</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import apiClient from '@/apiClient'
import UserUsageDialog from '@/components/admin/UserUsageDialog.vue'
import { formatBytes, formatMoney, formatNumber } from '@/utils/usageStats'

export default {
  name: 'UserManagement',
  components: { UserUsageDialog },
  data() {
    return {
      // sessions of a user (admin → users → «Сессии»)
      sessions: { visible: false, user: null, items: [], loading: false, busy: '' },
      // Статистика и себестоимость
      usageOverview: {},
      showUsageDialog: false,
      usageDialogUser: null,

      // Table state
      users: [],
      isLoading: false,
      selectedItems: [],
      plans: [],
      headers: [
        { title: '', key: 'checkbox', sortable: false, width: '36px' },
        { title: 'Пользователь', key: 'username', sortable: true },
        { title: 'Email', key: 'email', sortable: true },
        { title: 'Статус', key: 'status', sortable: true },
        // confirmation of the address: an icon with a tooltip, no header text
        { title: '', key: 'email_verified', sortable: false, width: '32px' },
        { title: 'Роли', key: 'roles', sortable: false },
        { title: 'Тариф', key: 'plan', sortable: false, width: '150px' },
        { title: 'Telegram', key: 'telegram_contact', sortable: false },
        { title: 'Сообщений', key: 'messages_total', sortable: false, align: 'end' },
        { title: 'Месяц, ₽', key: 'cost_rub_month', sortable: false, align: 'end' },
        { title: 'Всего, ₽', key: 'cost_rub_total', sortable: false, align: 'end' },
        { title: 'Место', key: 'storage_bytes_total', sortable: false, align: 'end' },
        { title: 'Регистрация', key: 'created_at', sortable: true },
        { title: 'Вход', key: 'last_login', sortable: true },
        { title: '', key: 'notes', sortable: false, width: '36px' },
        { title: 'Действия', key: 'actions', sortable: false }
      ],

      // Pagination
      currentPage: 1,
      totalPages: 1,
      totalItems: 0,
      limit: 20,
      sortBy: [{ key: 'username', order: 'asc' }],

      // Search and filters
      searchQuery: '',
      
      // Filter panel state
      showFilterPanel: true,
      panelVisible: true,
      userFilters: {
        searchQuery: '',
        isActive: null,
        isEmailConfirmed: null,
        isSuperuser: null,
        hasRoles: null,
        hasNotes: null,
        createdDateFrom: '',
        createdDateTo: '',
        lastLoginFrom: '',
        lastLoginTo: '',
        inviteCodeUsed: null
      },

      // Bulk actions
      selectedBulkAction: null,
      bulkActions: [
        { text: 'Активировать выбранных', value: 'activate' },
        { text: 'Деактивировать выбранных', value: 'deactivate' },
        { text: 'Назначить роль выбранным', value: 'assign_role' },
        { text: 'Удалить роль у выбранных', value: 'remove_role' }
      ],

      // Notes dialog state
      showNotesDialog: false,
      notesDialogUser: null,
      viewAsBusy: null,
      notesText: '',
      isSubmitting: false,

      // Roles dialog state
      showRolesDialog: false,
      rolesDialogUser: null,
      availableRoles: [],
      userRoles: [],
      isLoadingRoles: false,

      // Bulk role operations
      showBulkRoleDialog: false,
      bulkRoleAction: null,
      selectedRole: null,
      bulkAvailableRoles: [],

      // Snackbars
      showSuccessSnackbar: false,
      showErrorSnackbar: false,
      successMessage: '',
      errorMessage: ''
    }
  },
  computed: {
    // plans for the select: null - the default plan
    planItems() {
      const def = this.plans.find(p => p.is_default)
      return [{ id: null, title: def ? `${def.name} (по умолчанию)` : 'По умолчанию' },
        ...this.plans.filter(p => !p.is_default).map(p => ({ id: p.id, title: p.name }))]
    },
    offset() {
      return (this.currentPage - 1) * this.limit
    },
    
    availableRolesFiltered() {
      const userRoleIds = this.userRoles.map(role => role.id)
      return this.availableRoles.filter(role => !userRoleIds.includes(role.id))
    },

    // Master checkbox computed properties
    isAllSelected() {
      return this.users.length > 0 && this.selectedItems.length === this.users.length
    },

    isIndeterminate() {
      return this.selectedItems.length > 0 && this.selectedItems.length < this.users.length
    }
  },
  async mounted() {
    this.loadUsageOverview()
    await this.loadUsers()
    this.loadPlans()
    // Add ESC key listener
    document.addEventListener('keydown', this.handleKeydown)
  },
  beforeUnmount() {
    // Remove ESC key listener
    document.removeEventListener('keydown', this.handleKeydown)
  },
  methods: {
    async viewAs(user) {
      this.viewAsBusy = user.id
      try {
        await apiClient.post(`/admin/users/${user.id}/view-as`)
        // the whole page anew: the user, his spaces and menus instead of the admin ones
        window.location.assign('/dashboard')
      } catch (e) {
        this.errorMessage = 'Не удалось открыть просмотр: ' + (e.response?.data?.detail || e.message)
        this.showErrorSnackbar = true
        this.viewAsBusy = null
      }
    },
    async openSessions(user) {
      this.sessions = { visible: true, user, items: [], loading: true, busy: '' }
      try {
        const resp = await apiClient.get(`/admin/users/${user.id}/sessions`)
        this.sessions.items = resp.data.items
      } catch (e) {
        this.errorMessage = 'Ошибка загрузки сессий: ' + (e.response?.data?.detail || e.message)
        this.showErrorSnackbar = true
      } finally {
        this.sessions.loading = false
      }
    },
    async endSession(session) {
      this.sessions.busy = session.session_id
      try {
        await apiClient.delete(`/admin/users/${this.sessions.user.id}/sessions/${session.session_id}`)
        this.sessions.items = this.sessions.items.filter(s => s.session_id !== session.session_id)
        this.successMessage = 'Сессия завершена'
        this.showSuccessSnackbar = true
      } catch (e) {
        this.errorMessage = 'Не удалось завершить сессию: ' + (e.response?.data?.detail || e.message)
        this.showErrorSnackbar = true
      } finally {
        this.sessions.busy = ''
      }
    },
    async endAllSessions() {
      this.sessions.busy = 'all'
      try {
        const resp = await apiClient.delete(`/admin/users/${this.sessions.user.id}/sessions`)
        this.sessions.items = []
        this.successMessage = `Завершено сессий: ${resp.data.ended}`
        this.showSuccessSnackbar = true
      } catch (e) {
        this.errorMessage = 'Не удалось завершить сессии: ' + (e.response?.data?.detail || e.message)
        this.showErrorSnackbar = true
      } finally {
        this.sessions.busy = ''
      }
    },
    formatSessionTime(value) {
      return value ? new Date(value).toLocaleString('ru-RU', { dateStyle: 'short', timeStyle: 'short' }) : '—'
    },
    // Master checkbox methods
    toggleSelectAll(value) {
      if (value) {
        // Select all users on current page
        this.selectedItems = this.users.map(user => user.id)
      } else {
        // Clear all selections
        this.selectedItems = []
      }
    },

    // Filter panel methods
    togglePanel() {
      this.panelVisible = !this.panelVisible
    },

    expandPanel() {
      if (!this.panelVisible) {
        this.panelVisible = true
      }
    },

    // Data loading
    async loadUsers() {
      this.isLoading = true
      try {
        const params = {
          limit: this.limit,
          offset: this.offset
        }
        
        // Apply filters
        if (this.userFilters.searchQuery || this.searchQuery) {
          params.search = this.userFilters.searchQuery || this.searchQuery
        }
        
        if (this.userFilters.isActive !== null) {
          params.is_active = this.userFilters.isActive
        }
        
        if (this.userFilters.isEmailConfirmed !== null) {
          params.is_email_confirmed = this.userFilters.isEmailConfirmed
        }
        
        if (this.userFilters.isSuperuser !== null) {
          params.is_superuser = this.userFilters.isSuperuser
        }
        
        if (this.userFilters.createdDateFrom) {
          params.created_from = this.userFilters.createdDateFrom
        }
        
        if (this.userFilters.createdDateTo) {
          params.created_to = this.userFilters.createdDateTo
        }
        
        if (this.userFilters.lastLoginFrom) {
          params.last_login_from = this.userFilters.lastLoginFrom
        }
        
        if (this.userFilters.lastLoginTo) {
          params.last_login_to = this.userFilters.lastLoginTo
        }
        
        if (this.userFilters.inviteCodeUsed !== null) {
          params.invite_code_used = this.userFilters.inviteCodeUsed
        }
        
        if (this.userFilters.hasRoles !== null) {
          params.has_roles = this.userFilters.hasRoles
        }
        
        if (this.userFilters.hasNotes !== null) {
          params.has_notes = this.userFilters.hasNotes
        }
        
        const response = await apiClient.get('/admin/users', { params })
        this.users = response.data.users
        this.totalItems = response.data.total
        this.totalPages = Math.ceil(this.totalItems / this.limit)
        
      } catch (error) {
        this.showError('Ошибка загрузки пользователей: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.isLoading = false
      }
    },

    // Статистика и себестоимость
    formatBytes,
    formatMoney,
    formatNumber,
    formatDay(value) {
      return value ? new Date(value).toLocaleDateString('ru-RU') : ''
    },
    async loadUsageOverview() {
      try {
        const response = await apiClient.get('/admin/usage/overview')
        this.usageOverview = response.data
      } catch (error) {
        this.showError('Ошибка загрузки себестоимости: ' + (error.response?.data?.detail || error.message))
      }
    },
    openUsageDialog(event, { item }) {
      // Клик по кнопкам и флажкам строки — их собственное действие, не окно статистики
      if (event.target.closest('button, input, a, .v-selection-control')) return
      this.usageDialogUser = item
      this.showUsageDialog = true
    },

    // Pagination
    async onPageChange(page) {
      this.currentPage = page
      await this.loadUsers()
    },

    // Sorting
    async updateSort(sortBy) {
      this.sortBy = sortBy
      await this.loadUsers()
    },

    // User status management
    async loadPlans() {
      try {
        this.plans = (await apiClient.get('/admin/plans')).data.items
      } catch (error) {
        this.plans = []
      }
    },

    async setPlan(user, planId) {
      try {
        await apiClient.put(`/admin/users/${user.id}/plan`, { plan_id: planId })
        user.plan_id = planId
        this.showSuccess(`Тариф ${user.email} изменён`)
      } catch (error) {
        this.showError('Не удалось сменить тариф: ' + (error.response?.data?.detail || error.message))
      }
    },

    async deleteUser(user) {
      if (!confirm(`Удалить пользователя ${user.email}? Удаляются его роли, сессии, настройки уведомлений и «Мои источники»; согласия сохраняются. Отменить нельзя.`)) return
      try {
        await apiClient.delete(`/admin/users/${user.id}`)
        this.showSuccess(`Пользователь ${user.email} удалён`)
        await this.loadUsers()
      } catch (error) {
        this.showError('Не удалось удалить: ' + (error.response?.data?.detail || error.message))
      }
    },

    async toggleUserStatus(user) {
      const action = user.is_active ? 'деактивировать' : 'активировать'
      
      try {
        await apiClient.patch(`/admin/users/${user.id}/status`, {
          is_active: !user.is_active
        })
        
        const actionText = user.is_active ? 'деактивирован' : 'активирован'
        this.showSuccess(`Пользователь "${user.username}" ${actionText}`)
        await this.loadUsers()
        
      } catch (error) {
        this.showError('Ошибка изменения статуса: ' + (error.response?.data?.detail || error.message))
      }
    },

    // Notes management
    openNotesDialog(user) {
      this.notesDialogUser = user
      this.notesText = user.admin_notes || ''
      this.showNotesDialog = true
    },

    closeNotesDialog() {
      this.showNotesDialog = false
      this.notesDialogUser = null
      this.notesText = ''
    },

    async saveNotes() {
      if (!this.notesDialogUser) return
      
      this.isSubmitting = true
      try {
        await apiClient.patch(`/admin/users/${this.notesDialogUser.id}/notes`, {
          admin_notes: this.notesText || null
        })
        
        this.showSuccess('Примечания успешно сохранены')
        await this.loadUsers()
        this.closeNotesDialog()
        
      } catch (error) {
        this.showError('Ошибка сохранения примечаний: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.isSubmitting = false
      }
    },

    // Role management
    async manageRoles(user) {
      this.rolesDialogUser = user
      this.showRolesDialog = true
      await this.loadRolesData()
    },

    async loadRolesData() {
      this.isLoadingRoles = true
      try {
        // Load all available roles
        const rolesResponse = await apiClient.get('/admin/roles')
        this.availableRoles = rolesResponse.data.roles

        // Load user's current roles
        const userRolesResponse = await apiClient.get(`/admin/users/${this.rolesDialogUser.id}/roles`)
        this.userRoles = userRolesResponse.data.roles

      } catch (error) {
        this.showError('Ошибка загрузки ролей: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.isLoadingRoles = false
      }
    },

    async assignRole(role) {
      if (!this.rolesDialogUser) return
      
      this.isSubmitting = true
      try {
        await apiClient.post(`/admin/users/${this.rolesDialogUser.id}/roles`, {
          user_id: this.rolesDialogUser.id,
          role_id: role.id
        })
        
        this.showSuccess(`Роль "${role.name}" назначена пользователю "${this.rolesDialogUser.username}"`)
        await this.loadRolesData()
        await this.loadUsers() // Refresh main table
        
      } catch (error) {
        const errorMessage = error.response?.data?.detail || error.message
        if (errorMessage.includes('уже имеет роль')) {
          this.showError(`Пользователь "${this.rolesDialogUser.username}" уже имеет роль "${role.name}"`)
        } else {
          this.showError('Ошибка назначения роли: ' + errorMessage)
        }
      } finally {
        this.isSubmitting = false
      }
    },

    async removeRole(role) {
      if (!this.rolesDialogUser) return
      
      this.isSubmitting = true
      try {
        await apiClient.delete(`/admin/users/${this.rolesDialogUser.id}/roles/${role.id}`)
        
        this.showSuccess(`Роль "${role.name}" удалена у пользователя "${this.rolesDialogUser.username}"`)
        await this.loadRolesData()
        await this.loadUsers() // Refresh main table
        
      } catch (error) {
        this.showError('Ошибка удаления роли: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.isSubmitting = false
      }
    },

    closeRolesDialog() {
      this.showRolesDialog = false
      this.rolesDialogUser = null
      this.availableRoles = []
      this.userRoles = []
    },

    // Bulk operations
    async executeBulkAction() {
      if (!this.selectedBulkAction || this.selectedItems.length === 0) return
      
      // Handle role operations separately
      if (this.selectedBulkAction === 'assign_role' || this.selectedBulkAction === 'remove_role') {
        this.bulkRoleAction = this.selectedBulkAction
        this.showBulkRoleDialog = true
        await this.loadAvailableRoles()
        return
      }
      
      const actionText = this.bulkActions.find(a => a.value === this.selectedBulkAction)?.text
      if (!confirm(`${actionText} для ${this.selectedItems.length} пользователей?`)) return
      
      try {
        const isActivate = this.selectedBulkAction === 'activate'
        
        for (const userId of this.selectedItems) {
          await apiClient.patch(`/admin/users/${userId}/status`, {
            is_active: isActivate
          })
        }
        
        this.showSuccess(`Выполнено: ${actionText}`)
        this.selectedItems = []
        this.selectedBulkAction = null
        await this.loadUsers()
        
      } catch (error) {
        this.showError('Ошибка выполнения операции: ' + (error.response?.data?.detail || error.message))
      }
    },

    async loadAvailableRoles() {
      this.isLoadingRoles = true
      try {
        const rolesResponse = await apiClient.get('/admin/roles')
        this.bulkAvailableRoles = rolesResponse.data.roles || []
        console.log('Loaded bulk roles:', this.bulkAvailableRoles)
      } catch (error) {
        this.showError('Ошибка загрузки ролей: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.isLoadingRoles = false
      }
    },

    async executeBulkRoleAction() {
      if (!this.selectedRole || this.selectedItems.length === 0) return
      
      const actionText = this.bulkRoleAction === 'assign_role' ? 'назначение роли' : 'удаление роли'
      
      this.isSubmitting = true
      try {
        let successCount = 0
        let errorCount = 0
        
        for (const userId of this.selectedItems) {
          try {
            if (this.bulkRoleAction === 'assign_role') {
              await apiClient.post(`/admin/users/${userId}/roles`, {
                user_id: userId,
                role_id: this.selectedRole.id
              })
            } else {
              await apiClient.delete(`/admin/users/${userId}/roles/${this.selectedRole.id}`)
            }
            successCount++
          } catch (error) {
            errorCount++
            const errorMessage = error.response?.data?.detail || error.message
            console.error(`Error processing user ${userId}:`, errorMessage)
          }
        }
        
        if (successCount > 0) {
          let message = `${actionText.charAt(0).toUpperCase() + actionText.slice(1)} "${this.selectedRole.name}": успешно ${successCount}`
          if (errorCount > 0) {
            message += `, ошибок ${errorCount}`
          }
          this.showSuccess(message)
        }
        
        if (errorCount > 0 && successCount === 0) {
          this.showError(`Все операции завершились ошибкой. Возможно, пользователи уже имеют/не имеют эту роль.`)
        }
        
        this.selectedItems = []
        this.selectedBulkAction = null
        this.closeBulkRoleDialog()
        await this.loadUsers()
        
      } catch (error) {
        this.showError('Ошибка bulk операции: ' + (error.response?.data?.detail || error.message))
      } finally {
        this.isSubmitting = false
      }
    },

    closeBulkRoleDialog() {
      this.showBulkRoleDialog = false
      this.bulkRoleAction = null
      this.selectedRole = null
      this.bulkAvailableRoles = []
    },

    // Utility methods
    getStatusChipColor(user) {
      if (!user.is_active) return 'error'
      if (user.is_superuser) return 'purple'
      return 'success'
    },

    getStatusText(user) {
      if (!user.is_active) return 'Неактивен'
      if (user.is_superuser) return 'Суперпользователь'
      return 'Активен'
    },

    formatDate(dateString) {
      if (!dateString) return ''
      try {
        const date = new Date(dateString)
        return date.toLocaleDateString('ru-RU', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }) + ' ' + date.toLocaleTimeString('ru-RU', {
          hour: '2-digit',
          minute: '2-digit'
        })
      } catch (error) {
        return dateString
      }
    },

    // Message helpers
    showSuccess(message) {
      this.successMessage = message
      this.showSuccessSnackbar = true
    },

    showError(message) {
      this.errorMessage = message
      this.showErrorSnackbar = true
    },

    // Filter methods
    onFilterChange() {
      this.currentPage = 1
      this.loadUsers()
    },

    clearFilters() {
      this.userFilters = {
        searchQuery: '',
        isActive: null,
        isEmailConfirmed: null,
        isSuperuser: null,
        hasRoles: null,
        hasNotes: null,
        createdDateFrom: '',
        createdDateTo: '',
        lastLoginFrom: '',
        lastLoginTo: '',
        inviteCodeUsed: null
      }
      this.searchQuery = ''
      this.currentPage = 1
      this.loadUsers()
    },

    // Keyboard event handlers
    handleKeydown(event) {
      if (event.key === 'Escape') {
        if (this.showNotesDialog) {
          this.closeNotesDialog()
        } else if (this.showRolesDialog) {
          this.closeRolesDialog()
        } else if (this.showBulkRoleDialog) {
          this.closeBulkRoleDialog()
        } else if (this.showFilterPanel) {
          this.showFilterPanel = false
        }
      }
    }
  }
}
</script>

<style scoped>
/* narrow cells (02.11.2026): the table fits the page without scrolling sideways */
.users-table :deep(th),
.users-table :deep(td) {
  padding-left: 6px !important;
  padding-right: 6px !important;
}
.users-table :deep(td .v-btn--icon.v-btn--size-small) {
  width: 28px;
  height: 28px;
}
.cursor-pointer {
  cursor: pointer;
}
</style>